import React from "react";
import { SignalRProvider, useSignalRContext } from "@resideo/blueprint-react";
import { useAuth0 } from "@auth0/auth0-react";

interface SignalRMethod {
  url: string;
  params?: string;
}
interface SignalRSubscriberProps {
  children: React.ReactNode;
  subscribeMethod?: SignalRMethod;
  unsubscribeMethod?: SignalRMethod;
  isAuthenticated?: boolean;
}

const SignalRSubscriber: React.FC<SignalRSubscriberProps> = ({
  children,
  subscribeMethod,
  unsubscribeMethod,
  isAuthenticated = false,
}) => {
  const { invokeMethod } = useSignalRContext();

  React.useEffect(() => {
    if (isAuthenticated && invokeMethod && subscribeMethod) {
      if (subscribeMethod.params) {
        invokeMethod(subscribeMethod.url, subscribeMethod.params);
        return;
      }
      invokeMethod(subscribeMethod.url);
    }

    return () => {
      if (isAuthenticated && unsubscribeMethod) {
        if (unsubscribeMethod.params) {
          invokeMethod(unsubscribeMethod.url, unsubscribeMethod.params);
          return;
        }
        invokeMethod(unsubscribeMethod.url);
      }
    };
  }, [isAuthenticated, invokeMethod]);

  return <>{children}</>;
};

export const SignalRProviderWrapper: React.FC<{
  children: React.ReactNode;
  subscribeMethod?: SignalRMethod;
  unsubscribeMethod?: SignalRMethod;
}> = ({ children, subscribeMethod, unsubscribeMethod }) => {
  // Try to get Auth0 context, but don't crash if it's not available (mock auth mode)
  let isAuthenticated = false;
  let getAccessTokenSilently: (() => Promise<string>) | undefined;
  
  try {
    const auth0Context = useAuth0();
    isAuthenticated = auth0Context.isAuthenticated;
    getAccessTokenSilently = auth0Context.getAccessTokenSilently;
  } catch (error) {
    // Auth0Provider not available (mock auth mode) - use fallback values
    console.log('[SignalRProviderWrapper] Auth0 context not available, using mock mode');
    isAuthenticated = false;
    getAccessTokenSilently = async () => {
      throw new Error('Auth0 not available in mock mode');
    };
  }

  return (
    <SignalRProvider
      isAuthenticated={isAuthenticated}
      getAccessTokenSilently={getAccessTokenSilently}
      signalRUrl={import.meta.env.VITE_SIGNALR_URL || ""}
      signalREnabled={true}
    >
      <SignalRSubscriber
        subscribeMethod={subscribeMethod}
        unsubscribeMethod={unsubscribeMethod}
        isAuthenticated={isAuthenticated}
      >
        {children}
      </SignalRSubscriber>
    </SignalRProvider>
  );
};
